import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useLazySearchDonorsQuery } from '@/app/services/donors';
import {
  useLazySearchAllDonorsQuery,
  useLazySearchDonorsByOwnerIdQuery,
} from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Donor } from '@/rtk-query.gql';

import './DonorAutoComplete.css';

type DonorAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: Donor;
  ownerId?: string;
  searchAllDonors?: boolean;
};

export const DonorAutoComplete = ({
  className,
  label,
  defaultValue,
  ownerId,
  searchAllDonors,
  ...autoCompleteProps
}: DonorAutoCompleteProps) => {
  const [selected, setSelected] = useState<Donor | undefined>(defaultValue);
  const [donors, setDonors] = useState<Donor[]>([]);
  const [filtered, setFiltered] = useState<Donor[]>([]);
  const [inputValue, setInputValue] = useState<string | undefined>(
    defaultValue?.name || '',
  );

  const [query] = useLazySearchDonorsQuery();
  const [queryAll] = useLazySearchAllDonorsQuery();
  const [queryByOwnerId] = useLazySearchDonorsByOwnerIdQuery();

  useEffect(() => {
    if (defaultValue) {
      setInputValue(defaultValue.name as string);
      setSelected(defaultValue);
    } else if (defaultValue === null) {
      setInputValue('');
      setSelected(undefined);
    }
  }, [defaultValue, setInputValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      if (!event.query.trim()) {
        if (ownerId && !searchAllDonors) {
          const { data = [] } = await queryByOwnerId({
            search: '',
            selectionLimit: SELECTION_LIMIT,
            ownerId,
          });
          const formattedDonors = data.map((donor) => ({
            ...donor,
            name: `${donor.name} (${donor.registrationNumber ? `${donor.registrationNumber} - ` : ''}${donor.owner?.code})`,
          }));
          setFiltered(formattedDonors);
          return;
        }

        const { data = [] } = await queryAll({
          search: '',
          selectionLimit: SELECTION_LIMIT,
        });

        const formattedDonors = data.map((donor) => ({
          ...donor,
          name: `${donor.name} (${donor.owner?.code})`,
        }));
        setFiltered(formattedDonors);
        return;
      }

      if (event.query.length < MIN_SEARCH_LENGTH) {
        return;
      }

      if (ownerId && !searchAllDonors) {
        const { data = [] } = await queryByOwnerId({
          search: event.query,
          selectionLimit: SELECTION_LIMIT,
          ownerId,
        });

        const formattedDonors = data.map((donor) => ({
          ...donor,
          name: `${donor.name} (${donor.registrationNumber ? `${donor.registrationNumber} - ` : ''}${donor.owner?.code})`,
        }));
        setFiltered(formattedDonors);
        return;
      }

      const { data = [] } = await query({
        where: { name: { contains: event.query } },
        take: SELECTION_LIMIT,
        skip: 0,
      });

      const formattedDonors = (data as Donor[]).map((donor) => ({
        ...donor,
        name: `${donor.name} (${donor.owner?.code})`,
      }));
      setFiltered(formattedDonors);
    },
    [ownerId, queryAll, queryByOwnerId, searchAllDonors],
  );

  const handleSelect = (e: { value: Donor }) => {
    setSelected(e.value);
    setInputValue(e.value.name as string);
  };

  const handleChange = (e: { value: string }) => {
    setInputValue(e.value);
  };

  return (
    <div
      className={`[ donor-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
